import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
    Search,
    Filter,
    Check,
    CircleCheck,
    X,
    Calendar,
    Clock,
    AlertCircle,
    FileText,
    ChevronDown,
    DollarSign,
    Receipt,
    Download,
    Loader,
    User,
    Briefcase,
    RotateCcw,
    Eye,
} from "lucide-react";
import {
    getAdminNoteDeFraisInboxRequest,
    getAdminNoteDeFraisHistoryRequest,
    adminNoteDeFraisDecisionRequest,
    clearNoteDeFraisRequestMessage,
    marquerNoteDeFraisRembourseeRequest,
} from "../../../../redux/actions/noteDeFraisRequestActions";
import { fetchNoteDeFraisRequest } from "../../../../redux/actions/noteDeFraisActions";
import "../TraitementStyles/TraitementNotesFrais.css";
import "../TraitementStyles/TreatmentMesOrdres.css";
import { exportTreatmentExcel } from "./exportTreatmentExcel";
import useTreatmentAutoRefresh from "./useTreatmentAutoRefresh";
import { confirmTreatmentApproval } from "./confirmTreatmentApproval";
import { getRequestTreatedBy } from "./treatmentDecisionInfo";
import { useEffect, useMemo, useRef, useState } from "react";
import Swal from "sweetalert2";
import { API_BASE_URL } from "../../../../../api";
import { jsPDF } from "jspdf";
import latoRegularUrl from "../../../../fonts/Lato-Regular.ttf";
import latoBoldUrl from "../../../../fonts/Lato-Bold.ttf";

const getExpenseCategories = (note) => {
    const categories = [...new Set((note?.items || []).map(item => item.category).filter(Boolean))];
    return categories.length > 0 ? categories.join(", ") : note?.category || note?.requestType || "-";
};
const getExpenseItems = (note) => {
    if (note?.items?.length) return note.items;
    return [{
        id: note?.id,
        category: note?.category,
        montantDepense: note?.montantDepense,
        devise: note?.devise,
    }];
};
const getExpenseDescriptions = (note) => {
    const descriptions = (note?.items || []).map(item => item.description).filter(Boolean);
    return descriptions.length > 0 ? descriptions.join(" · ") : note?.description || "-";
};

const formatPdfDate = (date) => date
    ? new Date(`${String(date).slice(0, 10)}T12:00:00`).toLocaleDateString("fr-FR")
    : "—";

const normalizeExpenseCategory = (category = "") => String(category)
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const SearchableCombobox = ({ value, onChange, options, placeholder }) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState(value || "");
    const wrapperRef = useRef(null);

    useEffect(() => {
        setQuery(value || "");
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filteredOptions = useMemo(() => {
        if (!query) return options;
        const q = query.toLowerCase();
        return options.filter((o) => o.label.toLowerCase().includes(q));
    }, [options, query]);

    const handleSelect = (opt) => {
        setQuery(opt.label);
        onChange(opt.value);
        setOpen(false);
    };

    const handleInputChange = (e) => {
        setQuery(e.target.value);
        onChange(e.target.value);
        setOpen(true);
    };

    const handleClear = () => {
        setQuery("");
        onChange("");
        setOpen(false);
    };

    return (
        <div ref={wrapperRef} style={{ position: "relative", width: "100%" }}>
            <div style={{ position: "relative" }}>
                <input
                    type="text"
                    className="dcInput"
                    style={{ width: "100%", fontSize: "13px", paddingRight: 56 }}
                    placeholder={placeholder}
                    value={query}
                    onChange={handleInputChange}
                    onFocus={() => setOpen(true)}
                />
                <div style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center", gap: 2 }}>
                    {query && (
                        <button type="button" onClick={handleClear} style={{ border: "none", background: "none", cursor: "pointer", color: "#9ca3af", padding: 4, lineHeight: 0 }} title="Effacer">
                            <X size={13} />
                        </button>
                    )}
                    <button type="button" onClick={() => setOpen(o => !o)} style={{ border: "none", background: "none", cursor: "pointer", color: "#9ca3af", padding: 4, lineHeight: 0 }} title="Afficher la liste">
                        <ChevronDown size={14} />
                    </button>
                </div>
            </div>
            {open && filteredOptions.length > 0 && (
                <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.10)", zIndex: 30, maxHeight: 220, overflowY: "auto" }}>
                    {filteredOptions.map((opt) => {
                        const isSelected = opt.value === value;
                        return (
                            <div key={opt.value} onClick={() => handleSelect(opt)}
                                style={{ padding: "8px 12px", fontSize: 13, cursor: "pointer", background: isSelected ? "#eff6ff" : "#fff", color: isSelected ? "#204385" : "#374151", fontWeight: isSelected ? 600 : 400 }}
                                onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                                onMouseLeave={(e) => (e.currentTarget.style.background = isSelected ? "#eff6ff" : "#fff")}>
                                {opt.label}
                            </div>
                        );
                    })}
                </div>
            )}
            {open && query && filteredOptions.length === 0 && (
                <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.10)", zIndex: 30, padding: "8px 12px", fontSize: 12, color: "#9ca3af" }}>
                    Aucun résultat
                </div>
            )}
        </div>
    );
};

const TraitementNotesFrais = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    // Redux state
    const { adminInbox, adminHistory, loading } = useSelector((state) => state.noteDeFraisRequests);
    const notesDeFrais = useSelector((state) => state.noteDeFrais?.notesDeFrais || []);
    const user = useSelector((state) => state.auth.user);

    const [activeTab, setActiveTab] = useState("enCours");
    const [filterCollab, setFilterCollab] = useState("");
    const [filterType, setFilterType] = useState("");
    const [filterDate, setFilterDate] = useState("");

    const handleResetFilters = () => {
        setFilterCollab("");
        setFilterType("");
        setFilterDate("");
    };

    const [showRejectModal, setShowRejectModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedNote, setSelectedNote] = useState(null);
    const [motifRejet, setMotifRejet] = useState("");

    // Charger les données au montage
    useEffect(() => {
        dispatch(getAdminNoteDeFraisInboxRequest());
        dispatch(getAdminNoteDeFraisHistoryRequest());
        dispatch(fetchNoteDeFraisRequest());
    }, [dispatch]);

    useTreatmentAutoRefresh(() => {
        dispatch(getAdminNoteDeFraisInboxRequest({ silent: true }));
        dispatch(getAdminNoteDeFraisHistoryRequest({ silent: true }));
    });

    const formatDate = (dateStr) => {
        if (!dateStr) return "-";
        const date = new Date(dateStr);
        return date.toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "short",
            year: "numeric"
        });
    };

    const formatMontant = (montant, devise = "MAD") => {
        return new Intl.NumberFormat("fr-MA", {
            style: "decimal",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(montant || 0) + " dhs";
    };

    const getSoldeAvance = (note) => Number(note?.soldeAvance ?? ((Number(note?.avance) || 0) - (Number(note?.montantDepense) || 0)));
    const getLibelleSoldeAvance = (note) => {
        const solde = getSoldeAvance(note);
        return solde > 0 ? "À rendre aux RH" : solde < 0 ? "À recevoir des RH" : "Aucun solde d'avance";
    };
    const getCouleurSoldeAvance = (note) => getSoldeAvance(note) > 0 ? "#b45309" : getSoldeAvance(note) < 0 ? "#0369a1" : "#64748b";

    const getDownloadUrl = (path) =>
        `${API_BASE_URL}/api/files/download?path=${encodeURIComponent(path || "")}`;

    const handleDownloadExpensePdf = async (note) => {
        try {
            const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "landscape" });
            let pdfFont = "helvetica";
            try {
                const toBase64 = (buffer) => {
                    const bytes = new Uint8Array(buffer);
                    let binary = "";
                    for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
                    return btoa(binary);
                };
                const [regular, bold] = await Promise.all([
                    fetch(latoRegularUrl).then((response) => response.arrayBuffer()),
                    fetch(latoBoldUrl).then((response) => response.arrayBuffer()),
                ]);
                doc.addFileToVFS("Lato-Regular.ttf", toBase64(regular));
                doc.addFileToVFS("Lato-Bold.ttf", toBase64(bold));
                doc.addFont("Lato-Regular.ttf", "Lato", "normal");
                doc.addFont("Lato-Bold.ttf", "Lato", "bold");
                pdfFont = "Lato";
            } catch (fontError) {
                console.warn("Police Lato indisponible pour le PDF, police de secours utilisée.", fontError);
            }

            const width = 297;
            const margin = 12;
            const right = width - margin;
            const currency = note.devise || "MAD";
            const amount = (value) => new Intl.NumberFormat("fr-MA", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(value) || 0);
            const allItems = note.items?.length > 0 ? note.items : [{ category: note.category, dateDepense: note.dateDepense, montantDepense: note.montantDepense, description: note.description }];
            // Les colonnes de la fiche reprennent les catégories configurées dans
            // notes_de_frais, et non une liste de catégories figée dans le PDF.
            const categoriesByKey = new Map();
            (notesDeFrais || []).forEach((expenseType) => {
                const key = normalizeExpenseCategory(expenseType.category);
                if (key) categoriesByKey.set(key, expenseType.category);
            });
            // Inclure également une catégorie présente sur une demande historique
            // qui ne serait plus configurée aujourd'hui.
            allItems.forEach((item) => {
                const key = normalizeExpenseCategory(item.category);
                if (key && !categoriesByKey.has(key)) categoriesByKey.set(key, item.category);
            });
            const expenseColumns = [...categoriesByKey.entries()].map(([key, label]) => ({ key, label }));
            const rowsByDate = new Map();
            allItems.forEach((item) => {
                const category = normalizeExpenseCategory(item.category);
                const date = item.dateDepense || note.dateDepense || "";
                const row = rowsByDate.get(date) || {
                    date,
                    amounts: Object.fromEntries(expenseColumns.map(({ key }) => [key, 0])),
                    observation: "",
                };
                if (category) row.amounts[category] = (row.amounts[category] || 0) + (Number(item.montantDepense) || 0);
                if (item.description) {
                    row.observation = [row.observation, item.description].filter(Boolean).join(" · ");
                }
                rowsByDate.set(date, row);
            });
            const rows = [...rowsByDate.values()].sort((a, b) => String(a.date).localeCompare(String(b.date)));
            const total = allItems.reduce((sum, item) => sum + (Number(item.montantDepense) || 0), 0);
            const advance = Number(note.avance) || 0;
            const balance = advance - total;
            // Une note associée prend sa période directement de l'ordre de mission.
            const periodDate = note.ordreMissionId && note.ordreMissionDateDebut
                ? note.ordreMissionDateDebut
                : (rows[0]?.date || note.dateDepense || note.createdAt);
            const monthLabel = periodDate
                ? new Date(`${String(periodDate).slice(0, 10)}T12:00:00`).toLocaleDateString("fr-FR", { month: "long" })
                : "—";
            const missionObject = note.ordreMissionId ? (note.ordreMissionObjet || "—") : "—";
            const missionCity = note.ordreMissionId ? (note.ordreMissionVille || "—") : "—";
            const setText = (size = 8, bold = false) => { doc.setFont(pdfFont, bold ? "bold" : "normal"); doc.setFontSize(size); };
            const text = (value, x, y, size = 8, bold = false, options = {}) => { setText(size, bold); doc.text(String(value || "—"), x, y, options); };
            const label = (name, value, x, y, valueX) => { text(`${name} :`, x, y, 8, true); text(value, valueX, y, 8, false); };

            let y = 15;
            text("ANNEXE 2", right, y, 8, true, { align: "right" });
            y += 7;
            doc.setFont("times", "bold"); doc.setFontSize(16);
            doc.text("NOTE DE FRAIS DE DÉPLACEMENT", width / 2, y, { align: "center" });
            const titleWidth = doc.getTextWidth("NOTE DE FRAIS DE DÉPLACEMENT");
            doc.line((width - titleWidth) / 2, y + 1.4, (width + titleWidth) / 2, y + 1.4);
            y += 12;
            label("SERVICE", note.employeeServiceName, margin, y, 42); label("NOM", note.employeeName, 155, y, 173); y += 6;
            label("OBJET", missionObject, margin, y, 42); label("VILLE", missionCity, 155, y, 173); y += 6;
            label("MOIS", monthLabel, margin, y, 42); label("ANNÉE", periodDate ? String(periodDate).slice(0, 4) : "—", 155, y, 173); y += 7;

            const columns = [["Jours", 19], ...expenseColumns.map(({ label }) => [label, 22]), ["Total\ngénéral", 22], ["Observations", 47]];
            const scale = (right - margin) / columns.reduce((sum, [, colWidth]) => sum + colWidth, 0);
            const drawCell = (x, top, cellWidth, height, value, { bold = false, align = "center", size = 7 } = {}) => {
                doc.rect(x, top, cellWidth, height); setText(size, bold);
                const lines = String(value ?? "").split("\n").flatMap((line) => doc.splitTextToSize(line, Math.max(cellWidth - 3, 2)));
                const startY = top + height / 2 - ((lines.length - 1) * 2.3) + 1.2;
                lines.forEach((line, index) => doc.text(line, align === "left" ? x + 1.5 : x + cellWidth / 2, startY + index * 4.6, { align }));
            };
            let x = margin;
            columns.forEach(([name, colWidth]) => { const cellWidth = colWidth * scale; drawCell(x, y, cellWidth, 10, name, { bold: true, size: 6.5 }); x += cellWidth; });
            y += 10;
            const displayRows = [...rows, ...Array(Math.max(0, 10 - rows.length)).fill(null)].slice(0, 10);
            displayRows.forEach((row) => {
                const lineTotal = row ? Object.values(row.amounts).reduce((sum, value) => sum + value, 0) : 0;
                const values = row
                    ? [formatPdfDate(row.date), ...expenseColumns.map(({ key }) => amount(row.amounts[key])), amount(lineTotal), row.observation]
                    : Array(columns.length).fill("");
                x = margin;
                columns.forEach(([, colWidth], index) => { const cellWidth = colWidth * scale; drawCell(x, y, cellWidth, 7, values[index], { align: index === columns.length - 1 ? "left" : "center", size: 6.5 }); x += cellWidth; });
                y += 7;
            });
            y += 6;
            const summaryX = 207;
            [["Total général", total], ["Avance", advance], [balance >= 0 ? "Solde à rendre" : "Solde à recevoir", Math.abs(balance)]].forEach(([name, value]) => {
                doc.rect(summaryX, y, 42, 7); doc.rect(summaryX + 42, y, 36, 7);
                text(name, summaryX + 2, y + 4.6, 8, true); text(`${amount(value)} ${currency}`, summaryX + 76, y + 4.6, 8, true, { align: "right" }); y += 7;
            });
            y += 4; text(`Total des frais : ${amount(total)} ${currency}`, margin, y, 8, true); y += 15;
            const signatureWidth = (right - margin) / 4;
            ["Visa de l'intéressé", "Visa du Responsable", "Visa Département Financier", "Visa de la Direction"].forEach((title, index) => {
                const xPos = margin + index * signatureWidth; doc.rect(xPos, y, signatureWidth, 25); text(title, xPos + signatureWidth / 2, y + 6, 8, true, { align: "center" });
            });
            doc.save(`Note_de_frais_${note.ordreMissionCode || note.id}.pdf`);
        } catch (error) {
            console.error("Erreur de génération de la fiche de note de frais", error);
            Swal.fire({ icon: "error", title: "Erreur", text: "La fiche de note de frais n'a pas pu être générée." });
        }
    };

    const getStatutBadge = (statut) => {
        const config = {
            PENDING_MANAGER: { label: "En attente du manager", bg: "#fff3cd", color: "#856404" },
            PENDING_ADMIN: { label: "En attente de l'admin RH", bg: "#fff3cd", color: "#856404" },
            APPROVED: { label: "Approuvé", bg: "#d4edda", color: "#155724" },
            REJECTED: { label: "Rejeté", bg: "#f8d7da", color: "#721c24" },
            PAID: { label: "Solde réglé", bg: "#d4edda", color: "#155724" },
        };
        const { label, bg, color } = config[statut] || config.PENDING_ADMIN;
        return (
            <span className="tnfBadge" style={{ backgroundColor: bg, color }}>
                {label}
            </span>
        );
    };

    const handleApprove = (noteId) => {
        confirmTreatmentApproval(() => {
            dispatch(adminNoteDeFraisDecisionRequest({
                id: noteId,
                decision: true,
                comment: "",
            }));

            if (showDetailsModal) closeDetails();

            setTimeout(() => {
                dispatch(getAdminNoteDeFraisInboxRequest());
                dispatch(getAdminNoteDeFraisHistoryRequest());
            }, 800);
        });
    };

    const handleReject = (note) => {
        setSelectedNote(note);
        setShowRejectModal(true);
    };

    const handleMarkAsReimbursed = (note) => {
        if (!note) return;

        Swal.fire({
            title: "Confirmer le règlement du solde",
            text: `Confirmez-vous que le solde avec ${note.employeeName || "le collaborateur"} a été réglé ?`,
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#10b981",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Oui, solde réglé",
            cancelButtonText: "Annuler",
        }).then((result) => {
            if (!result.isConfirmed) return;

            dispatch(marquerNoteDeFraisRembourseeRequest({ id: note.id }));
            if (showDetailsModal) closeDetails();

            setTimeout(() => {
                dispatch(getAdminNoteDeFraisHistoryRequest());
            }, 800);
        });
    };

    const confirmReject = () => {
        if (!motifRejet.trim() || !selectedNote) {
            alert("Veuillez saisir un motif de rejet");
            return;
        }

        dispatch(adminNoteDeFraisDecisionRequest({
            id: selectedNote.id,
            decision: false,
            comment: motifRejet,
        }));

        setShowRejectModal(false);
        setMotifRejet("");
        setSelectedNote(null);

        if (showDetailsModal) {
            closeDetails();
        }

        setTimeout(() => {
            dispatch(getAdminNoteDeFraisInboxRequest());
            dispatch(getAdminNoteDeFraisHistoryRequest());
        }, 1000);
    };

    const showDetails = (note) => {
        setSelectedNote(note);
        setShowDetailsModal(true);
    };

    const closeDetails = () => {
        setShowDetailsModal(false);
        setSelectedNote(null);
    };

    const closeReject = () => {
        setShowRejectModal(false);
        setMotifRejet("");
        setSelectedNote(null);
    };

    // Combiner les données selon l'onglet actif
    const allNotes = useMemo(() => {
        if (activeTab === "enCours") {
            return (adminInbox || []).filter(note => note.status === "PENDING_ADMIN");
        }

        return (adminHistory || []).filter(n => {
            if (activeTab === "approuve") return n.status === "APPROVED" || n.status === "PAID";
            if (activeTab === "rejete") return n.status === "REJECTED";
            return true;
        });
    }, [activeTab, adminInbox, adminHistory]);

    const collaborateurOptions = useMemo(() => {
        const map = new Map();
        (allNotes || []).forEach((n) => {
            const name = n.employeeName || "";
            if (name && !map.has(name)) {
                map.set(name, { value: name, label: n.employeeMatricule ? `${name} (${n.employeeMatricule})` : name });
            }
        });
        return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
    }, [allNotes]);

    const typeOptions = useMemo(() => {
        return [...new Set((allNotes || []).flatMap((n) => getExpenseCategories(n).split(", ")).filter(Boolean))]
            .sort().map((t) => ({ value: t, label: t }));
    }, [allNotes]);

    const getFilteredNotes = () => {
        let filtered = allNotes;

        if (filterCollab) {
            const val = filterCollab.toLowerCase();
            filtered = filtered.filter((n) =>
                (n.employeeName || "").toLowerCase().includes(val) ||
                (n.employeeMatricule || "").toLowerCase().includes(val)
            );
        }
        if (filterType) {
            const val = filterType.toLowerCase();
            filtered = filtered.filter((n) =>
                getExpenseCategories(n).toLowerCase().includes(val)
            );
        }
        if (filterDate) {
            filtered = filtered.filter((n) => {
                if (!n.dateDepense && !n.createdAt) return false;
                const dStr = n.dateDepense || n.createdAt;
                return String(dStr).slice(0, 10) === filterDate;
            });
        }

        return filtered;
    };

    const tabs = useMemo(() => {
        // Compteur "En cours" = nombre de notes réellement en attente (même filtre que allNotes)
        const enCoursCount = (adminInbox || []).filter(note => note.status === "PENDING_ADMIN").length;

        const approuveCount = (adminHistory || []).filter(n =>
            n.status === "APPROVED" || n.status === "PAID"
        ).length;

        const rejeteCount = (adminHistory || []).filter(n =>
            n.status === "REJECTED"
        ).length;

        return [
            { id: "enCours", label: "En cours", count: enCoursCount },
            { id: "approuve", label: "Approuvées", count: approuveCount },
            { id: "rejete", label: "Rejetées", count: rejeteCount },
        ];
    }, [adminInbox, adminHistory]);

    const typesDepense = useMemo(() => {
        const types = new Set();
        allNotes.forEach(n => {
            getExpenseCategories(n).split(", ").filter(Boolean).forEach(type => types.add(type));
        });
        return Array.from(types);
    }, [allNotes]);

    const departements = useMemo(() => {
        const depts = new Set();
        allNotes.forEach(n => {
            if (n.employeeMatricule) depts.add(n.employeeMatricule);
        });
        return Array.from(depts);
    }, [allNotes]);

    const totalMontant = useMemo(
        () => getFilteredNotes().reduce((sum, n) => sum + (n.montantDepense || 0), 0),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [allNotes, filterCollab, filterType, filterDate]
    );

    const depassementsCount = useMemo(() => {
        return adminInbox?.filter(n => {
            const plafond = n.noteDeFrais?.montantMinimum;
            return plafond && n.montantDepense > plafond;
        }).length || 0;
    }, [adminInbox]);

    const urgentCount = useMemo(() => {
        return adminInbox?.filter(n => n.urgent).length || 0;
    }, [adminInbox]);

    const filteredNotes = getFilteredNotes();

    const getCouleurByCategorie = (categorie) => {
        const colors = {
            TRANSPORT: "#3b82f6",
            HEBERGEMENT: "#8b5cf6",
            RESTAURATION: "#10b981",
            AUTRE: "#f59e0b",
        };
        return colors[categorie] || "#6b7280";
    };

    if (loading && allNotes.length === 0) {
        return (
            <div className="tnfPage">
                <div className="tnfWrap" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
                    <Loader size={32} className="spin" />
                </div>
            </div>
        );
    }

    return (
        <div className="tnfPage">
            <div className="tnfWrap">
                {/* Header */}
                <div className="tnfHeader">
                    <div>
                        <h1>{t("Traitement des notes de frais")}</h1>
                        <p>Gérez et validez les notes de frais en attente de validation RH</p>
                    </div>
                </div>

                {/* Stats */}
                {/*<div className="tnfStats">*/}
                {/*    <div className="tnfStatCard">*/}
                {/*        <div>*/}
                {/*            <div className="tnfStatLabel">En attente</div>*/}
                {/*            <div className="tnfStatValue">*/}
                {/*                {(adminInbox || []).filter(note =>*/}
                {/*                    note.status === "PENDING_ADMIN" ||*/}
                {/*                    note.status === "PENDING_MANAGER"*/}
                {/*                ).length || 0}*/}
                {/*            </div>*/}
                {/*        </div>*/}
                {/*        <Clock size={28} className="tnfStatIcon tnfStatIcon--warn" />*/}
                {/*    </div>*/}

                {/*    <div className="tnfStatCard">*/}
                {/*        <div>*/}
                {/*            <div className="tnfStatLabel">Montant total</div>*/}
                {/*            <div className="tnfStatValue tnfStatValue--money">{formatMontant(totalMontant)}</div>*/}
                {/*        </div>*/}
                {/*        <DollarSign size={28} className="tnfStatIcon tnfStatIcon--accent" />*/}
                {/*    </div>*/}

                {/*    /!*<div className="tnfStatCard">*!/*/}
                {/*    /!*    <div>*!/*/}
                {/*    /!*        <div className="tnfStatLabel">Urgentes</div>*!/*/}
                {/*    /!*        <div className="tnfStatValue tnfStatValue--orange">{urgentCount}</div>*!/*/}
                {/*    /!*    </div>*!/*/}
                {/*    /!*    <AlertCircle size={28} className="tnfStatIcon tnfStatIcon--orange" />*!/*/}
                {/*    /!*</div>*!/*/}

                {/*    <div className="tnfStatCard">*/}
                {/*        <div>*/}
                {/*            <div className="tnfStatLabel">Approuvées</div>*/}
                {/*            <div className="tnfStatValue tnfStatValue--ok">*/}
                {/*                {adminHistory?.filter(n => n.status === "APPROVED" || n.status === "PAID").length || 0}*/}
                {/*            </div>*/}
                {/*        </div>*/}
                {/*        <Check size={28} className="tnfStatIcon tnfStatIcon--ok" />*/}
                {/*    </div>*/}

                {/*    <div className="tnfStatCard">*/}
                {/*        <div>*/}
                {/*            <div className="tnfStatLabel">Rejetées</div>*/}
                {/*            <div className="tnfStatValue tnfStatValue--no">*/}
                {/*                {adminHistory?.filter(n => n.status === "REJECTED").length || 0}*/}
                {/*            </div>*/}
                {/*        </div>*/}
                {/*        <X size={28} className="tnfStatIcon tnfStatIcon--no" />*/}
                {/*    </div>*/}
                {/*</div>*/}

                {/* Filters Panel */}
                <div className="tnfCard" style={{ overflow: "visible" }}>
                    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: "16px", padding: "4px" }}>
                        <div style={{ minWidth: 220, flex: "1 1 220px" }}>
                            <label style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "4px" }}>Collaborateur / Matricule</label>
                            <SearchableCombobox value={filterCollab} onChange={setFilterCollab} options={collaborateurOptions} placeholder="Nom ou matricule..." />
                        </div>
                        <div style={{ minWidth: 200, flex: "1 1 200px" }}>
                            <label style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "4px" }}>Catégorie</label>
                            <SearchableCombobox value={filterType} onChange={setFilterType} options={typeOptions} placeholder="Rechercher une catégorie..." />
                        </div>
                        <div style={{ minWidth: 160, flex: "1 1 160px" }}>
                            <label style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "4px" }}>Date de demande</label>
                            <input type="date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} className="dcInput" style={{ width: "100%", fontSize: "13px" }} />
                        </div>
                        <div style={{ display: "flex", gap: 8, flexShrink: 0, alignItems: "flex-end" }}>
                            <button type="button" className="dcBtn dcBtn--outline treatmentExportBtn" style={{ padding: "10px 14px", display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", height: "38px", background: "#10b981", color: "white", border: "none", whiteSpace: "nowrap" }} onClick={() => exportTreatmentExcel(filteredNotes, "Notes_de_frais_admin")}><Download size={14} /> Exporter Excel</button>
                            <button type="button" onClick={handleResetFilters} className="dcBtn dcBtn--outline" style={{ padding: "10px 12px", display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", height: "38px", whiteSpace: "nowrap" }}>
                                <RotateCcw size={14} /> Réinitialiser
                            </button>
                        </div>
                    </div>
                </div>

                {/* Tabs + Table */}
                <div className="tnfCard">
                    <div className="tnfTabs">
                        {tabs.map((tab) => (
                            <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)} className={`tnfTab ${activeTab === tab.id ? "is-active" : ""}`}>
                                {tab.label}
                                <span className={`tnfCount ${activeTab === tab.id ? "is-active" : ""}`}>{tab.count}</span>
                            </button>
                        ))}
                    </div>

                    <div className="tnfTableWrap">
                        {filteredNotes.length === 0 ? (
                            <div className="tnfEmpty">
                                <AlertCircle size={46} />
                                <div>Aucune note de frais trouvée</div>
                            </div>
                        ) : (
                            <table className="tnfTable">
                                <thead>
                                    <tr>
                                        <th>Collaborateur</th>
                                        <th>Catégorie</th>
                                        <th>Description</th>
                                        {/*<th>Centre de coût</th>*/}
                                        <th>Date dépense</th>
                                        <th>Montant</th>
                                        <th>Règlement avance</th>
                                        {(activeTab === "approuve" || activeTab === "rejete") && <th>Traitée par</th>}
                                        {activeTab === "rejete" && <th>Motif</th>}
                                        <th className="is-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredNotes.map((note) => (
                                        <tr
                                            key={note.id}
                                            className={`${note.urgent ? "tnfRowUrgent" : ""}`}
                                            title={note.urgent ? "Note urgente" : ""}
                                        >
                                            <td>
                                                <div className="tnfName">
                                                    <span className="tnfPrimary">
                                                        {note.employeeName || "N/A"}
                                                    </span>
                                                    {note.urgent && <span className="tnfFire">🔥</span>}
                                                </div>
                                                <div className="tnfSub">
                                                    {note.employeeMatricule || "-"}
                                                </div>
                                            </td>

                                            <td>
                                                <div style={{ display: "grid", gap: 5 }}>
                                                    {getExpenseItems(note).map((item, index) => (
                                                        <div key={item.id || `${note.id}-${index}`} className="tnfType">
                                                            <span
                                                                className="tnfDot"
                                                                style={{ backgroundColor: getCouleurByCategorie(item.category) }}
                                                            />
                                                            <span className="tnfPrimary">{item.category || "-"}</span>
                                                            <span className="tnfSub" style={{ marginLeft: 4 }}>— {formatMontant(item.montantDepense, item.devise || note.devise)}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </td>

                                            <td className="tnfDesc">
                                                <div className="tnfTrunc" title={getExpenseDescriptions(note)}>
                                                    {getExpenseDescriptions(note)}
                                                </div>
                                            </td>

                                            <td><span className="tnfMuted">{formatDate(note.dateDepense)}</span></td>

                                            <td>
                                                <div className="tnfAmount">
                                                    {note.items?.length > 1 && <div className="tnfSub">Total</div>}
                                                    <div className="tnfPrimary tnfAmountMain">{formatMontant(note.montantDepense)}</div>
                                                    {note.montantValide && note.montantValide !== note.montantDepense && (
                                                        <div className="tnfSub">Validé: {formatMontant(note.montantValide)}</div>
                                                    )}
                                                </div>
                                            </td>

                                            <td>
                                                <div className="tnfSub" style={{ color: getCouleurSoldeAvance(note), fontWeight: 700 }}>{getLibelleSoldeAvance(note)}</div>
                                                {getSoldeAvance(note) !== 0 && <div className="tnfPrimary">{formatMontant(Math.abs(getSoldeAvance(note)))}</div>}
                                            </td>

                                            {(activeTab === "approuve" || activeTab === "rejete") && (
                                                <td><span className="tnfMuted">{getRequestTreatedBy(note)}</span></td>
                                            )}

                                            {activeTab === "rejete" && <td><span className="tnfMuted">{note.adminComment || note.managerComment || "—"}</span></td>}

                                            <td className="is-right" onClick={(e) => e.stopPropagation()}>
                                                <div className="tnfRowActions">
                                                    {activeTab === "enCours" && (
                                                        <>
                                                            <button
                                                                type="button"
                                                                className="tnfActionBtn tnfActionBtn--ok"
                                                                onClick={() => handleApprove(note.id)}
                                                                title="Approuver"
                                                                disabled={loading}
                                                            >
                                                                <Check size={16} />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="tnfActionBtn tnfActionBtn--no"
                                                                onClick={() => handleReject(note)}
                                                                title="Refuser"
                                                                disabled={loading}
                                                            >
                                                                <X size={16} />
                                                            </button>
                                                        </>
                                                    )}
                                                    {activeTab === "approuve" && note.status === "APPROVED" && (
                                                        <button
                                                            type="button"
                                                            className="tnfActionBtn tnfActionBtn--ok"
                                                            onClick={() => handleMarkAsReimbursed(note)}
                                                            title="Régler le solde"
                                                            disabled={loading}
                                                        >
                                                            <Receipt size={16} />
                                                        </button>
                                                    )}
                                                    {activeTab === "approuve" && note.status === "PAID" && (
                                                        <span
                                                            className="tnfActionBtn tnfActionBtn--ok"
                                                            style={{ cursor: "default" }}
                                                            title="Solde réglé"
                                                            aria-label="Solde réglé"
                                                        >
                                                            <CircleCheck size={16} />
                                                        </span>
                                                    )}
                                                    {activeTab === "approuve" && (
                                                        <button type="button" className="tnfActionBtn" onClick={() => handleDownloadExpensePdf(note)} title="Télécharger la fiche de note de frais" style={{ background: "#ecfdf5", color: "#059669", border: "1px solid #a7f3d0" }}>
                                                            <Download size={16} />
                                                        </button>
                                                    )}
                                                    <button
                                                        type="button"
                                                        className="tnfIconBtn"
                                                        onClick={() => showDetails(note)}
                                                        title="Voir détails"
                                                    >
                                                        <Eye size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>

            {/* DETAILS MODAL */}
            {showDetailsModal && selectedNote && (
                <div className="tnfModal">
                    <div className="tnfModal__panel" role="dialog" aria-modal="true">
                        <div className="tnfModal__header">
                            <div className="tnfModal__headLeft">
                                <div className="tnfModal__icon">
                                    <FileText size={18} />
                                </div>
                                <div>
                                    <div className="tnfModal__title">Détails de la note de frais</div>
                                    <div className="tnfModal__sub">
                                        {selectedNote.employeeName || "N/A"}
                                    </div>
                                </div>
                            </div>

                            <button type="button" className="tnfIconBtn" onClick={closeDetails} aria-label="Fermer">
                                <X size={18} />
                            </button>
                        </div>

                        <div className="tnfModal__body">
                            {/* Alerte urgence */}
                            {selectedNote.urgent && (
                                <div className="tnfAlert tnfAlert--orange">
                                    <AlertCircle size={18} />
                                    <div>
                                        <div className="tnfAlert__title">Note de frais urgente</div>
                                        <div className="tnfAlert__text">Cette demande nécessite un traitement prioritaire</div>
                                    </div>
                                </div>
                            )}

                            {/* Informations collaborateur */}
                            <div className="tnfPanel">
                                <div className="tnfPanel__title">Informations collaborateur</div>
                                <div className="tnfGrid2">
                                    <div className="tnfInfo">
                                        <div className="tnfInfo__label">Nom complet</div>
                                        <div className="tnfInfo__value">
                                            {selectedNote.employeeName || "-"}
                                        </div>
                                    </div>
                                    <div className="tnfInfo">
                                        <div className="tnfInfo__label">Matricule</div>
                                        <div className="tnfInfo__value">{selectedNote.employeeMatricule || "-"}</div>
                                    </div>
                                    <div className="tnfInfo">
                                        <div className="tnfInfo__label">ID Employé</div>
                                        <div className="tnfInfo__value">{selectedNote.employeeId || "-"}</div>
                                    </div>
                                    {/*<div className="tnfInfo">*/}
                                    {/*    <div className="tnfInfo__label">Centre de coût</div>*/}
                                    {/*    <div className="tnfInfo__value">{selectedNote.centreCouts || "-"}</div>*/}
                                    {/*</div>*/}
                                </div>
                            </div>

                            {/* Détails dépense */}
                            <div className="tnfBlock">
                                <div className="tnfBlock__title">Détails de la dépense</div>

                                <div className="tnfLine">
                                    <Receipt size={16} />
                                    <div className="tnfLine__content">
                                        <div className="tnfLine__label">Catégorie</div>
                                        <div className="tnfLine__value">
                                            {getExpenseCategories(selectedNote)}
                                        </div>
                                    </div>
                                </div>

                                <div className="tnfLine">
                                    <Calendar size={16} />
                                    <div className="tnfLine__content">
                                        <div className="tnfLine__label">Date dépense / soumission</div>
                                        <div className="tnfLine__value">
                                            {formatDate(selectedNote.dateDepense)} — soumise le {formatDate(selectedNote.createdAt)}
                                        </div>
                                    </div>
                                </div>

                                <div className="tnfLine">
                                    <DollarSign size={16} />
                                    <div className="tnfLine__content">
                                        <div className="tnfLine__label">Montant demandé</div>
                                        <div className="tnfLine__value">{formatMontant(selectedNote.montantDepense)}</div>
                                    </div>
                                </div>

                                <div className="tnfLine">
                                    <DollarSign size={16} />
                                    <div className="tnfLine__content">
                                        <div className="tnfLine__label">Règlement de l'avance</div>
                                        <div className="tnfLine__value" style={{ color: getCouleurSoldeAvance(selectedNote) }}>
                                            {getLibelleSoldeAvance(selectedNote)}{getSoldeAvance(selectedNote) !== 0 && ` : ${formatMontant(Math.abs(getSoldeAvance(selectedNote)))}`}
                                        </div>
                                    </div>
                                </div>

                                {selectedNote.montantValide && (
                                    <div className="tnfLine">
                                        <DollarSign size={16} />
                                        <div className="tnfLine__content">
                                            <div className="tnfLine__label">Montant validé</div>
                                            <div className="tnfLine__value">{formatMontant(selectedNote.montantValide)}</div>
                                        </div>
                                    </div>
                                )}

                                {/*<div className="tnfLine">*/}
                                {/*    <Briefcase size={16} />*/}
                                {/*    <div className="tnfLine__content">*/}
                                {/*        <div className="tnfLine__label">Centre de coût</div>*/}
                                {/*        <div className="tnfLine__value">{selectedNote.centreCouts || "-"}</div>*/}
                                {/*    </div>*/}
                                {/*</div>*/}

                                {selectedNote.ordreMissionLibelle && (
                                    <div className="tnfLine">
                                        <FileText size={16} />
                                        <div className="tnfLine__content">
                                            <div className="tnfLine__label">Ordre de mission</div>
                                            <div className="tnfLine__value">
                                                {selectedNote.ordreMissionCode} - {selectedNote.ordreMissionLibelle}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="tnfLine">
                                    <FileText size={16} />
                                    <div className="tnfLine__content">
                                        <div className="tnfLine__label">Description</div>
                                        <div className="tnfLine__value">{getExpenseDescriptions(selectedNote)}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Validation manager */}
                            {selectedNote.managerDecision !== null && (
                                <div className="tnfBlock">
                                    <div className="tnfBlock__title">Validation manager</div>
                                    <div className="tnfPanel">
                                        <div className="tnfGrid2">
                                            <div className="tnfInfo">
                                                <div className="tnfInfo__label">Manager</div>
                                                <div className="tnfInfo__value">
                                                    {selectedNote.managerName || "-"}
                                                </div>
                                            </div>
                                            <div className="tnfInfo">
                                                <div className="tnfInfo__label">Décision</div>
                                                <div className="tnfInfo__value">
                                                    {selectedNote.managerDecision ? (
                                                        <span style={{ color: "#155724" }}>✓ Approuvé</span>
                                                    ) : (
                                                        <span style={{ color: "#721c24" }}>✗ Rejeté</span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="tnfInfo">
                                                <div className="tnfInfo__label">Date</div>
                                                <div className="tnfInfo__value">{formatDate(selectedNote.managerDecisionAt)}</div>
                                            </div>
                                        </div>
                                        {selectedNote.managerComment && (
                                            <div style={{ marginTop: 12, padding: 12, background: "#f9fafb", borderRadius: 6 }}>
                                                <div className="tnfInfo__label">Commentaire</div>
                                                <div style={{ marginTop: 4 }}>{selectedNote.managerComment}</div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {selectedNote.justificatifAvance && (
                                <div className="tnfBlock">
                                    <div className="tnfBlock__title">Justificatif de l'avance</div>
                                    <div className="tnfDocs">
                                        <a className="tnfDoc" href={getDownloadUrl(selectedNote.justificatifAvance)} target="_blank" rel="noreferrer">
                                            <FileText size={18} /><div className="tnfDoc__meta"><div className="tnfDoc__name">{selectedNote.justificatifAvance}</div></div><Download size={16} className="tnfDoc__dl" />
                                        </a>
                                    </div>
                                </div>
                            )}

                            {/* Justificatifs */}
                            {selectedNote.justificatifs && selectedNote.justificatifs.length > 0 && (
                                <div className="tnfBlock">
                                    <div className="tnfBlock__title">Justificatifs ({selectedNote.justificatifs.length})</div>
                                    <div className="tnfDocs">
                                        {selectedNote.justificatifs.map((j, idx) => (
                                            <a key={idx} className="tnfDoc" href={getDownloadUrl(j)} target="_blank" rel="noreferrer">
                                                <FileText size={18} />
                                                <div className="tnfDoc__meta">
                                                    <div className="tnfDoc__name">{j}</div>
                                                </div>
                                                <Download size={16} className="tnfDoc__dl" />
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Informations de validation (historique) */}
                            {(selectedNote.status === "APPROVED" || selectedNote.status === "REJECTED" || selectedNote.status === "PAID") && (
                                <div className="tnfBlock">
                                    <div className="tnfBlock__title">Informations de validation RH</div>
                                    <div className="tnfPanel">
                                        <div className="tnfGrid2">
                                            <div className="tnfInfo">
                                                <div className="tnfInfo__label">Statut</div>
                                                <div className="tnfInfo__value">{getStatutBadge(selectedNote.status)}</div>
                                            </div>
                                            <div className="tnfInfo">
                                                <div className="tnfInfo__label">Date de validation</div>
                                                <div className="tnfInfo__value">{formatDate(selectedNote.adminDecisionAt)}</div>
                                            </div>
                                            <div className="tnfInfo">
                                                <div className="tnfInfo__label">Validé par</div>
                                                <div className="tnfInfo__value">
                                                    {selectedNote.adminName || "-"}
                                                </div>
                                            </div>
                                            {selectedNote.status === "APPROVED" && (
                                                <div className="tnfInfo">
                                                    <div className="tnfInfo__label">Montant validé</div>
                                                    <div className="tnfInfo__value tnfValueOk">
                                                        {formatMontant(selectedNote.montantValide || selectedNote.montantDepense)}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {selectedNote.adminComment && (
                                            <div className="tnfRejectReason">
                                                <div className="tnfRejectReason__label">Commentaire RH</div>
                                                <div className="tnfRejectReason__text">{selectedNote.adminComment}</div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {selectedNote.status === "PAID" && (
                                <div className="tnfBlock">
                                    <div className="tnfBlock__title">Informations de règlement</div>
                                    <div className="tnfPanel">
                                        <div className="tnfGrid2">
                                            <div className="tnfInfo">
                                                <div className="tnfInfo__label">Statut</div>
                                                <div className="tnfInfo__value">{getStatutBadge(selectedNote.status)}</div>
                                            </div>
                                            <div className="tnfInfo">
                                                <div className="tnfInfo__label">Date de règlement</div>
                                                <div className="tnfInfo__value">{formatDate(selectedNote.dateRemboursement)}</div>
                                            </div>
                                            <div className="tnfInfo">
                                                <div className="tnfInfo__label">Réglé par</div>
                                                <div className="tnfInfo__value">{selectedNote.rembourseParName || "-"}</div>
                                            </div>
                                            {selectedNote.referencePaiement && (
                                                <div className="tnfInfo">
                                                    <div className="tnfInfo__label">Référence de paiement</div>
                                                    <div className="tnfInfo__value">{selectedNote.referencePaiement}</div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="tnfModal__footer">
                            {activeTab === "enCours" && (
                                <>
                                    <button
                                        type="button"
                                        className="tnfBtn tnfBtn--ghostDanger"
                                        onClick={() => {
                                            closeDetails();
                                            handleReject(selectedNote);
                                        }}
                                    >
                                        Rejeter
                                    </button>

                                    <button
                                        type="button"
                                        className="tnfBtn tnfBtn--ok"
                                        onClick={() => {
                                            handleApprove(selectedNote.id);
                                            closeDetails();
                                        }}
                                    >
                                        Approuver
                                    </button>
                                </>
                            )}

                            {activeTab === "approuve" && selectedNote.status === "APPROVED" && (
                                <button
                                    type="button"
                                    className="tnfBtn tnfBtn--ok"
                                    onClick={() => handleMarkAsReimbursed(selectedNote)}
                                    disabled={loading}
                                >
                                    Régler le solde
                                </button>
                            )}

                            <button type="button" className="tnfBtn tnfBtn--ghost" onClick={closeDetails}>
                                Fermer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* REJECT MODAL */}
            {showRejectModal && selectedNote && (
                <div className="tnfModal">
                    <div className="tnfModal__panel tnfModal__panel--small" role="dialog" aria-modal="true">
                        <div className="tnfModal__header">
                            <div className="tnfModal__headLeft">
                                <div className="tnfModal__icon tnfModal__icon--danger">
                                    <AlertCircle size={18} />
                                </div>
                                <div>
                                    <div className="tnfModal__title">Refuser la note de frais</div>
                                    <div className="tnfModal__sub">
                                        {selectedNote.employeeName || "N/A"}
                                    </div>
                                </div>
                            </div>

                            <button type="button" className="tnfIconBtn" onClick={closeReject} aria-label="Fermer">
                                <X size={18} />
                            </button>
                        </div>

                        <div className="tnfModal__body">
                            <div className="tnfMiniCard">
                                <div className="tnfMiniRow">
                                    <span className="tnfMuted">Catégorie :</span>
                                    <span className="tnfStrong">
                                        {getExpenseCategories(selectedNote)}
                                    </span>
                                </div>
                                <div className="tnfMiniRow">
                                    <span className="tnfMuted">Montant :</span>
                                    <span className="tnfStrong tnfStrong--accent">
                                        {formatMontant(selectedNote.montantDepense)}
                                    </span>
                                </div>
                                <div className="tnfMiniRow">
                                    <span className="tnfMuted">Description :</span>
                                    <span className="tnfStrong">{getExpenseDescriptions(selectedNote)}</span>
                                </div>

                                {selectedNote.urgent && (
                                    <div className="tnfMiniWarn">
                                        <AlertCircle size={16} />
                                        <span>Note de frais urgente</span>
                                    </div>
                                )}
                            </div>

                            <label className="tnfLabel">
                                Motif du rejet <span className="tnfReq">*</span>
                            </label>
                            <textarea
                                value={motifRejet}
                                required
                                onChange={(e) => setMotifRejet(e.target.value)}
                                rows={5}
                                placeholder="Veuillez préciser le motif du rejet de cette note de frais..."
                                className="tnfTextarea"
                            />
                            <div className="tnfHelp">Ce motif sera communiqué au collaborateur</div>
                        </div>

                        <div className="tnfModal__footer">
                            <button type="button" className="tnfBtn tnfBtn--ghost" onClick={closeReject}>
                                Annuler
                            </button>
                            <button
                                type="button"
                                className="tnfBtn tnfBtn--danger"
                                onClick={confirmReject}
                                disabled={!motifRejet.trim() || loading}
                            >
                                {loading ? "En cours..." : "Confirmer le rejet"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TraitementNotesFrais;
